/* eslint-disable */
import { useQuery, useMutation } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions, QueryKey } from '@tanstack/react-query';

import { headerFetcher } from '../utils/Fetcher';
import type { RequestOptions } from '../utils/Fetcher';
const fetcher = headerFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
  ? T[number]
  : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
  ? U
  : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  V2_COURSE_CATEGORY : '/v2/course-category',
  V2_SESSION : '/v2/session',
  V2_MY_CATEGORY : '/v2/my-category'
} as const;
export function useV2MyCategoryUpdateMutation<TContext>({
    url,
    options,
    requestOptions,
  }: {
    url: string;
    options?: UseMutationOptions<V2MyCategoryUpdateResponse, unknown, V2MyCategoryUpdateVariables, TContext>;
    requestOptions?: RequestOptions;
  }) {
    const result = useMutation<V2MyCategoryUpdateResponse, unknown, V2MyCategoryUpdateVariables, TContext>(
      (variables) => fetcher.put(url, variables, requestOptions),
      options,
    )

    return { ...result, data: result.data?.data };
  }

export const fetchV2CourseCategory = async (url: string, requestOptions?: RequestOptions) => fetcher.get<V2CourseCategoryResponse>(url, requestOptions);

export function useV2CourseCategoryQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<V2CourseCategoryResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<V2CourseCategoryResponse, unknown>(
    queryKey,
    () => fetchV2CourseCategory(url, requestOptions),
    options
  );

  return { ...result, data: result.data?.data };
}

export const fetchV2Session = async (url: string, requestOptions?: RequestOptions) => fetcher.get<V2SessionResponse>(url, requestOptions);

export function useV2SessionQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<V2SessionResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<V2SessionResponse, unknown>(
    queryKey,
    () => fetchV2Session(url, requestOptions),
    options
  );

  return { ...result, data: result.data?.data };
}

export const fetchV2MyCategory = async (url: string, requestOptions?: RequestOptions) => fetcher.get<V2MyCategoryResponse>(url, requestOptions);

export function useV2MyCategoryQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<V2MyCategoryResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  const result = useQuery<V2MyCategoryResponse, unknown>(
    queryKey,
    () => fetchV2MyCategory(url, requestOptions),
    options
  );

  return { ...result, data: result.data?.data };
}

/**
 * created by ResponseSchemaGenerator
 */


export interface V2CourseCategory {
  data: CourseCategoryResponse[];
}
export interface CourseCategoryResponse {
  id: number;
  slug: string;
  title: string;
  icon: string;
  children: CourseCategoryChildrenDto[];
}
export interface CourseCategoryChildrenDto {
  id: number;
  slug: string;
  title: string;
  skills: CourseCategorySkillDto[];
}
export interface CourseCategorySkillDto {
  slug: string;
  title: string;
}
export interface V2Session {
  data: Maybe<SessionResponse>;
}
export interface SessionResponse {
  userId: number;
  userEmail: string;
  userName: string;
  userThumbnailUrl: string;
  isCompleteHidden: boolean;
  /**
   * 블랙유저 여부
   */
  isBlocked: boolean;
  role: SessionRoleDto;
  enrolmentApproveRequired: boolean;
  groupId?: number;
  groupName?: string;
  managedGroupId?: number;
  managedGroupName?: string;
}
export interface SessionRoleDto {
  isAdmin: boolean;
  isB2bManager: boolean;
  isB2b: boolean;
  isInstructor: boolean;
}
export interface V2MyCategory {
  data: MyCategoryResponse[];
}
export interface MyCategoryResponse {
  id: number;
  title: string;
  originalTitle: string;
  slug: string;
  type: MyCategoryType;
  parent?: MyCategoryParentDto;
}
export interface MyCategoryParentDto {
  title: string;
  originalTitle: string;
  slug: string;
}
export const enum MyCategoryType {
  PARENT = "PARENT",
  CHILD = "CHILD"
}
export interface V2MyCategoryUpdate {
  data: Maybe<string>;
}
export type V2CourseCategoryResponse = ApiResponse<V2CourseCategory['data']>;
export type V2SessionResponse = ApiResponse<V2Session['data']>;
export type V2MyCategoryResponse = ApiResponse<V2MyCategory['data']>;
export type V2MyCategoryUpdateResponse = ApiResponse<V2MyCategoryUpdate['data']>;

/**
 * created by DTOGenerator
 */


export interface MyCategorySaveRequest {
  categoryIds: number[];
}
export interface V2CourseCategoryQueryString {
  lang?: Lang;
}
export const enum Lang {
  ko = "ko",
  en = "en",
  ja = "ja",
  vi = "vi"
}
export interface V2MyCategoryQueryString {
  lang?: Lang;
}
export interface V2MyCategoryUpdateVariables {
  categoryIds: number[];
}
